<template>
    <div class="container">
        <div class="form-sections">
        <!-- Loading state -->
        <div v-if="loading" class="empty-state">
            <i class="fas fa-spinner fa-spin"></i>
            <p>Loading...</p>
        </div>

        <!-- Step Tracker Navigation -->
        <div v-if="!loading" class="tracker-container">
            <div class="tracker-steps">
            <div
                v-for="(step, index) in steps"
                :key="step.id"
                class="tracker-step"
                :class="{
                active: activeStep === step.id,
                completed: isStepCompleted(step.id),
                'last-step': index === steps.length - 1,
                }"
            >
                <div class="step-icon-container">
                <i :class="step.icon"></i>
                </div>
                <div class="step-label">{{ step.name }}</div>
            </div>
            </div>
            <div class="tracker-line"></div>
        </div>

        <!-- Dealer Details -->
        <div v-if="!loading && activeStep === 'dealer'" class="step-content">
        <div class="info-section full-width-section">
            <h5 class="section-title">Dealer Details</h5>

            <div class="dealer-details-grid">
            <div class="detail-item">
                <strong>Business Name:</strong>
                <span>{{ dealerDetails.businessName }}</span>
            </div>
            <div class="detail-item">
                <strong>Pricing Category:</strong>
                <span>{{ dealerDetails.dealerCatName }}</span>
            </div>
            <div class="detail-item">
                <strong>Dealer Type:</strong>
                <span>{{ dealerDetails.dealerType }}</span>
            </div>
            <div class="detail-item">
                <strong>Route:</strong>
                <span>{{ dealerDetails.routeName }}</span>
            </div>
            <div class="detail-item">
                <strong>Address:</strong>
                <span>{{ dealerDetails.address1 }}</span>
            </div>
            <div class="detail-item">
                <strong>Sales Executive:</strong>
                <span>{{ dealerDetails.salesExecutiveName }}</span>
            </div>
            <div class="detail-item">
                <strong>Distributor:</strong>
                <span>{{ dealerDetails.distributorName }}</span>
            </div>
            <div
                class="detail-item"
                v-if="dealerDetails.dealerGroupName"
            >
                <strong>Dealer Group:</strong>
                <span>{{ dealerDetails.dealerGroupName }}</span>
            </div>
            <div
                class="detail-item"
                v-if="dealerDetails.vatNo"
            >
                <strong>VAT Number:</strong>
                <span>{{ dealerDetails.vatNo }}</span>
            </div>
            <div
                class="detail-item"
                v-if="dealerDetails.svatNo"
            >
                <strong>SVAT Number:</strong>
                <span>{{ dealerDetails.svatNo }}</span>
            </div>
            </div>
        </div>

        <!-- Step Navigation - Only Next button for first step -->
        <div class="step-navigation">
            <div class="step-navigation-left">
            <!-- Empty on first step -->
            </div>
            <div class="step-navigation-right">
            <button type="button" class="btn btn-primary" @click="goToNextStep">
                Next
            </button>
            </div>
        </div>
        </div>

        <!-- Item Details -->
        <div v-if="!loading && activeStep === 'items'" class="step-content">
        <div class="info-section">
            <h5 class="section-title">Item Details</h5>

            <!-- Global Date Range Toggle -->
            <div class="global-date-toggle-container">
            <div class="global-date-toggle-label">
                Apply same date range to all items
            </div>
            <label class="toggle-switch">
                <input
                type="checkbox"
                v-model="applySameDateForAll"
                :true-value="1"
                :false-value="0"
                />
                <span class="toggle-slider"></span>
            </label>
            </div>

            <!-- Global Date Range Inputs - shown only when toggle is on -->
            <div v-if="applySameDateForAll" class="global-date-inputs">
            <div class="global-date-item">
                <label for="globalDateFrom">Date From<span class="req">*</span></label>
                <input
                  type="date"
                  id="globalDateFrom"
                  v-model="globalDateFrom"
                  :min="today"
                  @change="onGlobalDateFromChange"
                />
            </div>
            <div class="global-date-item">
                <label for="globalDateTo">Date To<span class="req">*</span></label>
                <input
                  type="date"
                  id="globalDateTo"
                  v-model="globalDateTo"
                  :min="getMinToDate(globalDateFrom)"
                  @change="applyGlobalDatesToItems"
                />
            </div>
            </div>

            <div class="stock-table-container">
            <table class="table-container">
                <thead>
                <tr>
                    <th class="item-image-col">Item Image</th>
                    <th class="item-name-col">Item Name</th>
                    <th class="qty-col">Quantity</th>
                    <th class="date-col">Date From</th>
                    <th class="date-col">Date To</th>
                </tr>
                </thead>
                <tbody>
                <tr v-for="(item, index) in stockItems" :key="index">
                    <td class="item-image-cell">
                    <div class="item-image-container">
                        <img
                        v-if="item.itemImage && isValidBase64Image(item.itemImage)"
                        :src="getBase64ImageSrc(item.itemImage)"
                        :alt="item.itemName"
                        class="item-image"
                        @error="handleStockImageError(index)"
                        />
                        <div v-else class="item-placeholder">
                        <i class="fa-solid fa-image"></i>
                        </div>
                    </div>
                    </td>
                    <td class="item-name-cell">{{ item.itemName || "N/A" }}</td>
                    <td class="qty-cell">
                    <input
                        type="number"
                        v-model="item.quantity"
                        placeholder="Qty"
                        class="qty-input"
                        min="0"
                    />
                    </td>
                    <td class="date-cell">
                        <input
                            type="date"
                            v-model="item.dateFrom"
                            class="date-input"
                            :min="today"
                            :disabled="applySameDateForAll"
                            @change="onItemDateFromChange(item)"
                        />
                    </td>
                    <td class="date-cell">
                        <input
                            type="date"
                            v-model="item.dateTo"
                            class="date-input"
                            :min="getMinToDate(item.dateFrom)"
                            :disabled="applySameDateForAll"
                        />
                    </td>
                </tr>
                </tbody>
            </table>

            <!-- Empty state -->
            <div v-if="stockLoading" class="empty-state">
                <i class="fas fa-spinner fa-spin"></i>
                <p>Loading items...</p>
            </div>

            <div v-else-if="stockItems.length === 0" class="empty-state">
                <i class="fas fa-box-open"></i>
                <p>No items found</p>
            </div>
            </div>
        </div>

        <div class="step-navigation">
            <div class="step-navigation-left">
            <button
                type="button"
                class="btn btn-secondary"
                @click="goToPreviousStep"
            >
                Back
            </button>
            </div>
            <div class="step-navigation-right">
            <button type="button" class="btn btn-primary" @click="goToNextStep">
                Next
            </button>
            </div>
        </div>
        </div>

        <!-- Documents -->
        <div v-if="!loading && activeStep === 'documents'" class="step-content">
          <div class="info-section full-width-section">
            <h5 class="section-title">Documents</h5>

            <div v-if="loadingTemplates" class="empty-state">
              <i class="fas fa-spinner fa-spin"></i>
              <p>Loading required documents...</p>
            </div>

            <div v-else-if="documentTemplates.length === 0" class="empty-state">
              <i class="fas fa-file-alt"></i>
              <p>No documents are required for this submission.</p>
            </div>

            <div v-else class="document-checklist">
              <div v-for="(row, index) in documentTemplates" :key="row.templateId" class="document-checklist-row">
                <div class="checklist-row-info">
                  <div class="checklist-row-name">
                    {{ row.documentName }}
                    <span v-if="row.isRequired == 1" class="req">*</span>
                  </div>
                  <a
                    v-if="row.templateFileUrl"
                    :href="row.templateFileUrl"
                    target="_blank"
                    rel="noopener"
                    class="download-template-btn"
                    :title="row.templateFileName"
                  >
                    <i class="fas fa-download"></i> Download Template
                  </a>
                </div>

                <div class="checklist-row-upload">
                  <div
                    class="document-image-container row-upload-box"
                    :class="{ 'drag-active': row.isDragging }"
                    @mouseenter="row.showActions = true"
                    @mouseleave="row.showActions = false"
                    @click="triggerReuploadInput(index)"
                    @dragover.prevent="row.isDragging = true"
                    @dragleave.prevent="row.isDragging = false"
                    @drop.prevent="handleReuploadDrop(index, $event)"
                  >
                    <img
                      v-if="row.uploadedFilePreview && isImageDocument(row.uploadedFileType)"
                      :src="row.uploadedFilePreview"
                      alt="Uploaded document"
                      class="document-image"
                    />
                    <div v-else-if="row.uploadedFilePreview && isPdfDocument(row.uploadedFileType)" class="pdf-preview">
                      <i class="fas fa-file-pdf"></i>
                      <span>{{ row.uploadedFileName }}</span>
                    </div>
                    <div v-else-if="row.uploadedFilePreview && isWordDocument(row.uploadedFileType)" class="pdf-preview">
                      <i class="fas fa-file-word"></i>
                      <span>{{ row.uploadedFileName }}</span>
                    </div>
                    <div v-else-if="row.uploadedFilePreview && isExcelDocument(row.uploadedFileType)" class="pdf-preview">
                      <i class="fas fa-file-excel"></i>
                      <span>{{ row.uploadedFileName }}</span>
                    </div>
                    <div v-else class="document-placeholder">
                      <i :class="row.isDragging ? 'fas fa-cloud-download-alt' : 'fas fa-file-upload'"></i>
                      <span>{{ row.isDragging ? "Drop file to upload" : "Click or Drag & Drop to Reupload" }}</span>
                      <small>PDF, JPG, PNG, JPEG, DOCX, XLSX</small>
                    </div>

                    <div
                      class="document-actions-overlay"
                      v-if="row.showActions && row.uploadedFilePreview"
                    >
                      <div class="action-buttons">
                        <button class="action-btn change-btn" @click.stop="triggerReuploadInput(index)" title="Change">
                          <i class="fas fa-sync"></i>
                        </button>
                        <button class="action-btn remove-btn" @click.stop="removeReuploadFile(index)" title="Remove">
                          <i class="fas fa-trash"></i>
                        </button>
                      </div>
                    </div>

                    <div v-if="row.uploading" class="upload-progress">
                      <div class="progress-bar">
                        <div class="progress-fill" :style="{ width: row.progress + '%' }"></div>
                      </div>
                      <span>{{ row.progress }}%</span>
                    </div>
                  </div>

                  <input
                    type="file"
                    ref="reuploadInputs"
                    @change="handleReuploadChange(index, $event)"
                    accept=".pdf,image/jpeg,image/jpg,image/png,.docx,.xlsx"
                    class="file-input-hidden"
                  />
                </div>
              </div>
            </div>
          </div>

          <div class="step-navigation">
            <div class="step-navigation-left">
              <button type="button" class="btn btn-secondary" @click="goToPreviousStep">Back</button>
            </div>
            <div class="step-navigation-right">
              <button type="button" class="btn btn-primary" @click="goToNextStep">Next</button>
            </div>
          </div>
        </div>

        <!-- Verification -->
        <div v-if="!loading && activeStep === 'verification'" class="step-content">
          <div class="info-section full-width-section">
          <h5 class="section-title">Verification</h5>
            <div class="comment-section">
              <div class="comment-item">
                <label for="remarks">Remarks</label>
                <textarea
                  id="remarks"
                  v-model="remarks"
                  class="comment-textarea"
                  placeholder="Enter remarks"
                  rows="4"
                ></textarea>
              </div>
            </div>
          </div>
          <div class="step-navigation">
          <div class="step-navigation-left">
              <button
              type="button"
              class="btn btn-secondary"
              @click="goToPreviousStep"
              >
              Back
              </button>
          </div>
          <div class="step-navigation-right">
              <button
              type="button"
              class="btn btn-success"
              @click="submitTrustIssue"
              :disabled="saving"
              >
              <span
                  v-if="saving"
                  class="spinner-border spinner-border-sm"
                  role="status"
                  aria-hidden="true"
              ></span>
              <span v-if="!saving">Submit</span>
              </button>
          </div>
        </div>
      </div>
      </div>
    </div>
</template>

<script>
import { 
  viewDealerDetails, 
  fetchCylindarItemNameList, 
  fetchDocumentTemplatesBySubmodule, 
  issueNewTrust,
} from "@/services/dataService";
import { useToast } from "vue-toastification";

export default {
    name: "IssueTrustForm",
    data() {
        return {
            dealerId: this.$route.params.id,
            token: localStorage.getItem("token_set") || "",
            loading: false,
            saving: false,

            // Step Management
            activeStep: "dealer",
            steps: [
                { id: "dealer", name: "Dealer Details", icon: "fas fa-user" },
                { id: "items", name: "Item Details", icon: "fas fa-boxes" },
                { id: "documents", name: "Documents", icon: "fas fa-file-alt" },
                { id: "verification", name: "Verification", icon: "fas fa-check-circle" },
            ],

            // Dealer Details
            dealerDetails: {
                profile_id: "",
                businessName: "",
                dealerCatName: "",
                dealerType: "",
                routeName: "",
                address1: "",
                salesExecutiveName: "",
                distributorName: "",
                dealerGroupName: "",
                vatNo: "",
                svatNo: "",
            },

            // Item Details
            itemsList: [],
            stockItems: [],
            stockImageErrors: {},
            applySameDateForAll: 0,
            globalDateFrom: "",
            globalDateTo: "",

            // Documents step
            subModuleId: 52,
            documentTemplates: [],
            loadingTemplates: false,
            templatesLoaded: false,

            // Verification step comments
            remarks: "",
            today: this.getTodayString(),
        };
    },

    setup() {
        const toast = useToast();
        return { toast };
    },

    created() {
        this.loadDealerDetails();
    },

    watch: {
        applySameDateForAll(newVal) {
            if (newVal) {
            this.applyGlobalDatesToItems();
            }
        },
    },

    methods: {
        // Load dealer details for display only
        async loadDealerDetails() {
            try {
                this.loading = true;
                const response = await viewDealerDetails(this.dealerId, this.token);
                const dealer = response.data?.data;

                if (dealer) {
                    this.dealerDetails = {
                        profile_id: dealer.profile_id || "",
                        businessName: dealer.business_name || "N/A",
                        dealerCatName: dealer.dealer_cat_name || "N/A",
                        dealerType: dealer.dealer_type || "N/A",
                        routeName: dealer.route_name || "N/A",
                        address1: dealer.address1 || "N/A",
                        salesExecutiveName: dealer.sales_executive_name || "N/A",
                        distributorName: dealer.distributor_name || "N/A",
                        dealerGroupName: dealer.dealer_group_name || "",
                        vatNo: dealer.vat_no || "",
                        svatNo: dealer.svat_no || "",
                    };
                    this.companyId = dealer.company_id || "";
                }
            } catch (error) {
                console.error("Error fetching dealer details:", error);
                this.toast.error("Failed to load dealer details.");
            } finally {
                this.loading = false;
            }
        },

        // Load items list for the dealer's company
        async loadItemsList(force = false) {
            if (this.stockLoading) {
                return false;
            }

            if (this.stockItemsLoaded && !force) {
                return true;
            }

            try {
                this.stockLoading = true;

                const response = await fetchCylindarItemNameList(this.companyId, 1, this.token);
                this.itemsList = response.data.data || [];

                this.processStockItems();

                this.stockItemsLoaded = true;
                return true;
            } catch (error) {
                console.error("Error fetching items list:", error);
                this.itemsList = [];
                this.stockItems = [];
                this.stockItemsLoaded = false;
                this.toast.error("Failed to load item list.");
                return false;
            } finally {
                this.stockLoading = false;
            }
        },

        ensureStockItemsLoaded() {
            return this.loadItemsList(false);
        },

        processStockItems() {
            if (!this.itemsList || this.itemsList.length === 0) {
                this.stockItems = [];
                return;
        }

        this.stockItems = this.itemsList.map((item) => {
            return {
            id: item.id,
            itemName: item.item_name || "Unknown Item",
            itemImage: item.image || null,
            quantity: "",
            dateFrom: "",
            dateTo: "",
            };
        });
        },

        // Applies the global date range to every item row (called on toggle-on and on input change)
        applyGlobalDatesToItems() {
            if (!this.applySameDateForAll) return;

            this.stockItems.forEach((item) => {
                item.dateFrom = this.globalDateFrom;
                item.dateTo = this.globalDateTo;
            });
        },

        isValidBase64Image(base64String) {
            if (!base64String) return false;
            const base64Regex =
                /^data:image\/(png|jpeg|jpg|gif|webp);base64,[A-Za-z0-9+/]+={0,2}$/;
            if (base64Regex.test(base64String)) {
                return true;
            }
            const pureBase64Regex = /^[A-Za-z0-9+/]+={0,2}$/;
            if (pureBase64Regex.test(base64String)) {
                return base64String.length > 0;
            }
            return false;
        },

        getBase64ImageSrc(base64String) {
            if (!base64String) return "";
            if (base64String.startsWith("data:image/")) {
                return base64String;
            }
            return `data:image/jpeg;base64,${base64String}`;
        },

        handleStockImageError(index) {
            this.$set(this.stockImageErrors, index, true);
        },

        formatDate(dateString) {
            if (!dateString) return "N/A";
            const date = new Date(dateString);
            return date.toLocaleDateString("en-US", {
                year: "numeric",
                month: "long",
                day: "numeric",
            });
        },

        // Step Navigation Methods
        async goToNextStep() {
          if (this.activeStep === "dealer") {
            const itemsLoaded = await this.ensureStockItemsLoaded();
            if (!itemsLoaded) return;
          }

          if (this.activeStep === "items") {
            if (!this.validateItemsStepWithToast()) return;   // ← new line

            const templatesLoaded = await this.ensureTemplatesLoaded();
            if (!templatesLoaded) return;
          }

          if (this.activeStep === "documents") {
            const missing = this.documentTemplates.find(
              (row) => row.isRequired == 1 && !row.uploadedFile
            );
            if (missing) {
              this.toast.error(`"${missing.documentName}" is required. Please reupload it before continuing.`);
              return;
            }
          }

          const currentIndex = this.steps.findIndex((step) => step.id === this.activeStep);
          if (currentIndex < this.steps.length - 1) {
            this.activeStep = this.steps[currentIndex + 1].id;
          }
        },

        goToPreviousStep() {
            const currentIndex = this.steps.findIndex(
                (step) => step.id === this.activeStep,
            );
            if (currentIndex > 0) {
                this.activeStep = this.steps[currentIndex - 1].id;
            }
        },

        isStepCompleted(stepId) {
            const stepIndex = this.steps.findIndex((step) => step.id === stepId);
            const currentIndex = this.steps.findIndex(
                (step) => step.id === this.activeStep,
            );
            return stepIndex < currentIndex;
        },

        async loadDocumentTemplates(force = false) {
          if (this.loadingTemplates) return false;
          if (this.templatesLoaded && !force) return true;

          try {
            this.loadingTemplates = true;
            const response = await fetchDocumentTemplatesBySubmodule(this.subModuleId, this.token);
            const templates = response.data?.data || [];

            this.documentTemplates = templates.map((template) => ({
              templateId: template.id,
              documentName: template.document_name,
              isRequired: template.is_required,
              templateFileUrl: template.template_file_url,
              templateFileName: template.template_file_name,

              // reupload state
              uploadedFile: null,
              uploadedFilePreview: null,
              uploadedFileType: null,
              uploadedFileName: "",
              isDragging: false,
              showActions: false,
              uploading: false,
              progress: 0,
            }));

            this.templatesLoaded = true;
            return true;
          } catch (error) {
            console.error("Error fetching document templates:", error);
            this.toast.error("Failed to load required documents.");
            this.documentTemplates = [];
            return false;
          } finally {
            this.loadingTemplates = false;
          }
        },

        ensureTemplatesLoaded() {
          return this.loadDocumentTemplates(false);
        },

        triggerReuploadInput(index) {
          const input = this.$refs.reuploadInputs[index];
          if (input) input.click();
        },

        handleReuploadChange(index, event) {
          const file = event.target.files[0];
          if (file) this.processReuploadFile(index, file);
        },

        handleReuploadDrop(index, event) {
          this.documentTemplates[index].isDragging = false;
          const file = event.dataTransfer.files && event.dataTransfer.files[0];
          if (file) this.processReuploadFile(index, file);
        },

        async processReuploadFile(index, file) {
          if (!this.validateReuploadFile(file)) return;

          const row = this.documentTemplates[index];
          row.uploadedFile = file;
          row.uploadedFileName = file.name;
          row.uploadedFileType = file.type;
          row.uploading = true;
          row.progress = 0;

          await this.simulateReuploadProgress(index);

          const reader = new FileReader();
          reader.onload = (e) => {
            row.uploadedFilePreview = e.target.result;
            row.uploading = false;
          };
          reader.readAsDataURL(file);
        },

        simulateReuploadProgress(index) {
          return new Promise((resolve) => {
            const interval = setInterval(() => {
              this.documentTemplates[index].progress += 10;
              if (this.documentTemplates[index].progress >= 100) {
                clearInterval(interval);
                resolve();
              }
            }, 100);
          });
        },

        removeReuploadFile(index) {
          const row = this.documentTemplates[index];
          row.uploadedFile = null;
          row.uploadedFilePreview = null;
          row.uploadedFileType = null;
          row.uploadedFileName = "";
          const input = this.$refs.reuploadInputs[index];
          if (input) input.value = "";
        },

        validateReuploadFile(file) {
          const maxSize = 5 * 1024 * 1024;
          if (file.size > maxSize) {
            this.toast.error("File size must be less than 5MB");
            return false;
          }
          const allowedTypes = [
            "image/jpeg", "image/jpg", "image/png", "application/pdf",
            "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          ];
          if (!allowedTypes.includes(file.type)) {
            this.toast.error("Please select a PDF, JPG, PNG, JPEG, DOCX, or XLSX file");
            return false;
          }
          return true;
        },

        isImageDocument(type) { return type && type.startsWith("image/"); },
        isPdfDocument(type) { return type === "application/pdf"; },
        isWordDocument(type) { return type === "application/vnd.openxmlformats-officedocument.wordprocessingml.document"; },
        isExcelDocument(type) { return type === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"; },

        validateItemsStepWithToast() {
          const itemsWithQty = this.stockItems.filter(
            (item) => item.quantity !== "" && item.quantity !== null && Number(item.quantity) > 0
          );

          if (itemsWithQty.length === 0) {
            this.toast.error("Please enter a quantity for at least one item.");
            return false;
          }

          if (this.applySameDateForAll) {
            if (!this.globalDateFrom || !this.globalDateTo) {
              this.toast.error("Please set both global Date From and Date To.");
              return false;
            }
          }

          for (const item of itemsWithQty) {
            if (!item.dateFrom || !item.dateTo) {
              this.toast.error(`Please set both Date From and Date To for "${item.itemName}".`);
              return false;
            }

            if (item.dateFrom < this.today) {
              this.toast.error(`Date From for "${item.itemName}" cannot be a past date.`);
              return false;
            }

            if (item.dateTo < this.today) {
              this.toast.error(`Date To for "${item.itemName}" cannot be a past date.`);
              return false;
            }

            if (item.dateTo <= item.dateFrom) {
              this.toast.error(`Date To must be after Date From for "${item.itemName}".`);
              return false;
            }
          }

          return true;
        },

        async submitTrustIssue() {
          try {
            this.saving = true;

            const toBase64 = (file) => new Promise((resolve, reject) => {
              const reader = new FileReader();
              reader.readAsDataURL(file);
              reader.onload = () => resolve(reader.result.split(",")[1]);
              reader.onerror = reject;
            });

            const items = this.stockItems
              .filter((item) => item.quantity !== "" && item.quantity !== null && Number(item.quantity) > 0)
              .map((item) => ({
                item_id: item.id,
                qty: Number(item.quantity),
                from_date: item.dateFrom,
                to_date: item.dateTo,
              }));

            const documents = [];
            for (const row of this.documentTemplates) {
              if (row.uploadedFile) {
                documents.push({
                  document_template_id: row.templateId,
                  document_file: await toBase64(row.uploadedFile),
                  document_file_name: row.uploadedFileName,
                });
              }
            }

            const payload = {
              dealer_id: Number(this.dealerDetails.profile_id),
              remarks: this.remarks,
              created_by: Number(localStorage.getItem("profile_id")),
              items,
              documents,
            };

            const response = await issueNewTrust(payload, this.token);

            if (response.data.status === "success") {
              this.toast.success(response.data.message);
              this.$router.push("/trust-management");
            } else {
              this.toast.error(response.data.message || "Validation Error!");
            }
          } catch (error) {
            console.error("Trust issue submission error:", error);
            this.toast.error("Failed to submit trust issue. Please try again.");
          } finally {
            this.saving = false;
          }
        },

        getTodayString() {
            const d = new Date();
            const month = String(d.getMonth() + 1).padStart(2, "0");
            const day = String(d.getDate()).padStart(2, "0");
            return `${d.getFullYear()}-${month}-${day}`;
        },

        getNextDay(dateString) {
            if (!dateString) return this.today;
            const [y, m, d] = dateString.split("-").map(Number);
            const next = new Date(y, m - 1, d + 1);
            const month = String(next.getMonth() + 1).padStart(2, "0");
            const day = String(next.getDate()).padStart(2, "0");
            return `${next.getFullYear()}-${month}-${day}`;
        },

        getMinToDate(dateFrom) {
            return dateFrom ? this.getNextDay(dateFrom) : this.today;
        },

        onGlobalDateFromChange() {
            if (this.globalDateTo && this.globalDateTo <= this.globalDateFrom) {
                this.globalDateTo = "";
            }
            this.applyGlobalDatesToItems();
        },

        onItemDateFromChange(item) {
            if (item.dateTo && item.dateTo <= item.dateFrom) {
                item.dateTo = "";
            }
        },
    },
};
</script>

<style scoped>
/* Tracker Styles */
.tracker-container {
  position: relative;
  margin-bottom: 2rem;
  width: 100%;
}

.tracker-steps {
  display: flex;
  justify-content: space-between;
  position: relative;
  z-index: 1;
}

.tracker-step {
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1;
  position: relative;
}

.step-icon-container {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #f3f4f6;
  border: 3px solid #e5e7eb;
  transition: all 0.3s ease;
  z-index: 2;
}

.tracker-step.active .step-icon-container {
  background-color: #1c66ad;
  border-color: #1c66ad;
  color: white;
  transform: scale(1.1);
}

.tracker-step.completed .step-icon-container {
  background-color: #28a745;
  border-color: #28a745;
  color: white;
}

.step-icon-container i {
  font-size: 1.2rem;
}

.step-label {
  margin-top: 0.5rem;
  font-size: 0.875rem;
  font-weight: 500;
  text-align: center;
  color: #6b7280;
  transition: color 0.3s ease;
}

.tracker-step.active .step-label {
  color: #1c66ad;
  font-weight: 600;
}

.tracker-step.completed .step-label {
  color: #28a745;
}

.tracker-line {
  position: absolute;
  top: 30px;
  left: 0;
  right: 0;
  height: 3px;
  background-color: #e5e7eb;
  z-index: 0;
}

/* Step Navigation Styles */
.step-navigation {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 2rem;
  padding-top: 1rem;
  border-top: 1px solid #e5e7eb;
  width: 100%;
}

.step-navigation-left {
  display: flex;
  justify-content: flex-start;
  flex: 1;
}

.step-navigation-right {
  display: flex;
  justify-content: flex-end;
  flex: 1;
}

.step-navigation:has(.step-navigation-left:empty) .step-navigation-right {
  justify-content: flex-end;
  width: 100%;
}

.btn {
  padding: 10px 20px;
  border: none;
  border-radius: 5px;
  font-size: 14px;
  cursor: pointer;
  transition: background-color 0.3s, transform 0.3s;
  min-width: 100px;
}

.btn-primary {
  background-color: #1c66ad;
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background-color: #155a9c;
  transform: scale(1.05);
}

.btn-secondary {
  background-color: #6b7280;
  color: white;
}

.btn-secondary:hover {
  background-color: #4b5563;
  transform: scale(1.05);
}

.btn-success {
  background-color: #28a745;
  color: white;
}

.btn-success:hover:not(:disabled) {
  background-color: #218838;
  transform: scale(1.05);
}

.btn-success:disabled {
  background-color: #9ca3af;
  cursor: not-allowed;
  transform: none;
}

.form-sections {
  display: flex;
  flex-direction: column;
  gap: 30px;
  width: 100%;
}

.section-title {
  margin-bottom: 20px;
  padding-bottom: 10px;
  border-bottom: 2px solid #ccc;
  color: #333;
}

.container {
  background-color: #ffffff;
  box-shadow: 0 0 15px rgba(0, 0, 0, 0.1);
  border-radius: 8px;
  padding: 20px;
  margin: 10px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  box-sizing: border-box;
}

.step-content {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  width: 100%;
}

.info-section {
  background: white;
  border-radius: 10px;
  padding: 1.5rem;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.05);
  margin-bottom: 1rem;
  width: 100%;
}

.full-width-section {
  width: 100%;
  max-width: 100%;
}

/* Dealer Details Display */
.dealer-details-grid {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.detail-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 15px;
  background-color: #f8f9fa;
  border-radius: 6px;
  border: 1px solid #e5e7eb;
}

.detail-item strong {
  color: #374151;
  font-weight: 600;
}

.detail-item span {
  color: #111827;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem;
  color: #6b7280;
}

.empty-state i {
  font-size: 3rem;
  margin-bottom: 1rem;
  color: #d1d5db;
}

/* Global Date Toggle */
.global-date-toggle-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  margin-bottom: 1rem;
  padding: 15px;
  background-color: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 8px;
}

.global-date-toggle-label {
  font-weight: bold;
  color: #333;
  font-size: 15px;
}

.global-date-inputs {
  display: flex;
  gap: 15px;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
}

.global-date-item {
  flex: 1;
  min-width: 200px;
  display: flex;
  flex-direction: column;
}

.global-date-item label {
  margin-bottom: 5px;
  font-weight: bold;
  font-size: 14px;
}

.global-date-item input {
  padding: 10px;
  border-radius: 5px;
  border: 1px solid #ccc;
  font-size: 14px;
}

.req {
  color: red;
  margin-left: 5px;
}

/* Reuse toggle-switch styles */
.toggle-switch {
  position: relative;
  display: inline-block;
  width: 60px;
  height: 34px;
}

.toggle-switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.toggle-slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #ccc;
  transition: 0.4s;
  border-radius: 34px;
}

.toggle-slider:before {
  position: absolute;
  content: "";
  height: 26px;
  width: 26px;
  left: 4px;
  bottom: 4px;
  background-color: white;
  transition: 0.4s;
  border-radius: 50%;
}

input:checked + .toggle-slider {
  background-color: #28a745;
}

input:checked + .toggle-slider:before {
  transform: translateX(26px);
}

/* Stock/Item Table */
.stock-table-container {
  overflow-x: auto;
}

.table-container {
  width: 100%;
  border-collapse: collapse;
  background: white;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.table-container th,
.table-container td {
  padding: 1rem;
  text-align: left;
  border-bottom: 1px solid #e5e7eb;
}

.table-container th {
  background-color: #f9fafb;
  font-weight: 600;
  color: #374151;
}

.item-image-cell {
  width: 80px;
}

.item-image-container {
  width: 60px;
  height: 60px;
  border-radius: 6px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.item-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.item-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #f3f4f6;
  color: #9ca3af;
}

.item-name-cell {
  font-weight: 500;
}

.qty-cell {
  width: 120px;
}

.qty-input {
  width: 100%;
  padding: 8px;
  border: 1px solid #ddd;
  border-radius: 4px;
  text-align: center;
  font-size: 14px;
}

.date-cell {
  width: 170px;
}

.date-input {
  width: 100%;
  padding: 8px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
}

.date-input:disabled {
  background-color: #f3f4f6;
  color: #9ca3af;
  cursor: not-allowed;
}

.qty-input:focus,
.date-input:focus {
  border-color: #007bff;
  outline: none;
  box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
}

.document-checklist {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

.document-checklist-row {
  display: flex;
  gap: 20px;
  align-items: center;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 1rem 1.25rem;
  background-color: #fafbfc;
}

.checklist-row-info {
  flex: 1;
  min-width: 0;
}

.checklist-row-name {
  font-weight: 600;
  color: #333;
  margin-bottom: 8px;
}

.download-template-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  border-radius: 20px;
  background-color: #eaf2ff;
  color: #1c66ad;
  text-decoration: none;
  font-size: 13px;
  font-weight: 600;
  border: 1px solid #cfe1fb;
}

.download-template-btn:hover {
  background-color: #1c66ad;
  color: #fff;
}

.checklist-row-upload {
  width: 220px;
  flex-shrink: 0;
}

.row-upload-box {
  height: 130px;
  position: relative;
  border-radius: 8px;
  overflow: hidden;
  cursor: pointer;
}

.document-image { width: 100%; height: 100%; object-fit: cover; }

.document-placeholder {
  width: 100%; height: 100%;
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  background-color: #f3f4f6; color: #6b7280;
  border: 2px dashed #d1d5db; border-radius: 8px; padding: 0.5rem; text-align: center;
}
.document-placeholder i { font-size: 1.4rem; margin-bottom: 0.3rem; color: #9ca3af; }
.document-placeholder span { font-size: 0.75rem; font-weight: 500; }
.document-placeholder small { display: block; margin-top: 3px; font-size: 0.65rem; color: #9ca3af; }

.row-upload-box.drag-active { box-shadow: 0 0 0 3px #1c66ad; }

.pdf-preview {
  width: 100%; height: 100%;
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  background-color: #fef3f2; color: #dc2626; padding: 0.5rem; text-align: center;
}
.pdf-preview i { font-size: 1.8rem; margin-bottom: 0.3rem; }
.pdf-preview span { font-size: 0.7rem; font-weight: 600; color: #374151; word-break: break-word; }

.document-actions-overlay {
  position: absolute; inset: 0;
  background: rgba(0,0,0,0.7);
  display: flex; align-items: center; justify-content: center;
}
.action-buttons { display: flex; gap: 10px; }
.action-btn {
  width: 32px; height: 32px; border-radius: 50%; border: none;
  display: flex; align-items: center; justify-content: center;
  color: white; cursor: pointer;
}
.change-btn { background: #4caf50; }
.remove-btn { background: #f44336; }

.upload-progress {
  position: absolute; bottom: 6px; left: 6px; right: 6px;
  background: rgba(255,255,255,0.9); padding: 4px; border-radius: 4px;
  display: flex; align-items: center; gap: 6px;
}
.progress-bar { flex: 1; height: 5px; background: #e5e7eb; border-radius: 3px; overflow: hidden; }
.progress-fill { height: 100%; background: #4caf50; }
.upload-progress span { font-size: 0.65rem; font-weight: 600; }

.file-input-hidden { display: none; }

.comment-section {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

.comment-item {
  display: flex;
  flex-direction: column;
}

.comment-item label {
  margin-bottom: 6px;
  font-weight: bold;
  font-size: 14px;
  color: #333;
}

.comment-textarea {
  width: 100%;
  padding: 10px;
  border-radius: 5px;
  border: 1px solid #ccc;
  font-size: 14px;
  font-family: inherit;
  resize: vertical;
  min-height: 80px;
  box-sizing: border-box;
  transition: border-color 0.3s, box-shadow 0.3s;
}

.comment-textarea:focus {
  border-color: #007bff;
  box-shadow: 0 0 5px rgba(38, 143, 255, 0.5);
  outline: none;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .step-icon-container {
    width: 50px;
    height: 50px;
  }

  .step-icon-container i {
    font-size: 1rem;
  }

  .step-label {
    font-size: 0.75rem;
  }

  .tracker-line {
    top: 25px;
  }

  .detail-item {
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
  }

  .step-navigation {
    flex-direction: column;
    gap: 10px;
  }

  .step-navigation-left,
  .step-navigation-right {
    justify-content: center;
    width: 100%;
  }

  .step-navigation .btn {
    width: 100%;
  }
}

@media (max-width: 480px) {
  .tracker-step {
    flex: 1;
  }

  .step-icon-container {
    width: 40px;
    height: 40px;
  }

  .step-icon-container i {
    font-size: 0.9rem;
  }

  .step-label {
    font-size: 0.7rem;
  }

  .tracker-line {
    top: 20px;
  }
}
</style>